import { useState, useCallback, useRef, useEffect } from 'react';
import { FlashbarProps } from '@amzn/awsui-components-core';
import { v4 as uuidv4 } from 'uuid';

export const useFlashbar = () => {
  const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Cleanup timeouts on unmount
  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
      timeouts.clear();
    };
  }, []);

  const displayFlashMessage = useCallback(
    (content: string, flashBarType: FlashbarProps.Type, isDismissible: boolean, messageId?: string) => {
      const flashbarId = messageId ? messageId : uuidv4();

      const newFlashbarItem = {
        id: flashbarId,
        type: flashBarType,
        content: content,
        dismissible: isDismissible,
        dismissLabel: 'Dismiss message',
        onDismiss: () => {
          setFlashbarItems((currentItems) => currentItems.filter((item) => item.id !== flashbarId));
          // Clear timeout if exists
          const timeout = timeoutsRef.current.get(flashbarId);
          if (timeout) {
            clearTimeout(timeout);
            timeoutsRef.current.delete(flashbarId);
          }
        },
      };

      setFlashbarItems((currentItems) => [...currentItems, newFlashbarItem]);

      if (flashBarType === 'success') {
        const timeout = setTimeout(() => {
          setFlashbarItems((currentItems) => currentItems.filter((item) => item.id !== flashbarId));
          timeoutsRef.current.delete(flashbarId);
        }, 5000);

        timeoutsRef.current.set(flashbarId, timeout);
      }
    },
    []
  );

  const clearSpecificFlashMessage = useCallback((messageId: string) => {
    setFlashbarItems((currentItems) => currentItems.filter((item) => item.id !== messageId));
    // Clear timeout if exists
    const timeout = timeoutsRef.current.get(messageId);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(messageId);
    }
  }, []);

  const clearAllMessages = useCallback(() => {
    setFlashbarItems([]);
    // Clear all timeouts
    timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    timeoutsRef.current.clear();
  }, []);

  return { flashbarItems, displayFlashMessage, clearSpecificFlashMessage, clearAllMessages };
};
